import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("Missing reset token. Request a new link.");
        }
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error("Password must be at least 8 characters");
        if (password !== confirm) return toast.error("Passwords do not match");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
            await checkAuth?.();
            toast.success("Password updated. You're signed in.");
            setTimeout(() => navigate("/"), 1200);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset link is invalid or expired");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ok-container py-16 max-w-md mx-auto" data-testid="reset-password-page">
            <div className="text-center mb-8">
                <div className="inline-block"><Logo /></div>
            </div>
            <div className="ok-card p-8">
                <h1 className="font-display text-2xl mb-1">Set a new password</h1>
                <p className="text-sm opacity-60 mb-6">Choose something strong, minimum 8 characters.</p>

                {done ? (
                    <div className="space-y-4" data-testid="reset-password-done">
                        <div className="ok-card p-4 flex items-center gap-3 border-[var(--ok-accent)]">
                            <CheckCircle2 className="w-5 h-5 text-[var(--ok-accent)]" />
                            <div className="text-sm">Password updated. Redirecting…</div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-widest opacity-60 block mb-1.5">New password</label>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="ok-input"
                                data-testid="reset-password-new"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-widest opacity-60 block mb-1.5">Confirm password</label>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="ok-input"
                                data-testid="reset-password-confirm"
                            />
                        </div>
                        <button type="submit" disabled={loading || !token} className="ok-btn-primary w-full justify-center" data-testid="reset-password-submit">
                            {loading ? "Updating…" : <>Update password <ArrowRight className="w-4 h-4" /></>}
                        </button>
                        <p className="text-xs text-center opacity-60 pt-2">
                            <Link to="/forgot-password" className="ok-link" data-testid="reset-password-request-new">Request a new link</Link>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}
